import type { QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { aiAgentKnowledgeBaseQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/queryKeyFactory";
import type { KnowledgeBaseResponse } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/types";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

export const processFile = async ({
    file,
    showMessage,
    queryClient,
    onFileUploaded,
    getPOValues,
}: {
    file: File;
    getPOValues: () => string[];
    showMessage: () => void;
    queryClient: QueryClient;
    onFileUploaded: () => void;
}) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
        const response = await salesApiService.aiAgent.knowledgeBase.uploadFile(formData);
        const fileId = (await response.json()) as string;
        AIAgentTrackingService.kb.trackFileUpload(file.name, "success");

        onFileUploaded();

        queryClient.setQueryData(
            aiAgentKnowledgeBaseQueryKeys.customization(),
            (oldData: KnowledgeBaseResponse): KnowledgeBaseResponse => ({
                ...oldData,
                files: [
                    ...oldData.files,
                    {
                        id: fileId,
                        name: file.name,
                        createdDate: dayjs(new Date()).format("YYYY-MM-DD"),
                        isProcessing: true,
                    },
                ],
            }),
        );

        const extractedPO = await salesApiService.aiAgent.knowledgeBase.getFileProductOfferings(fileId);
        AIAgentTrackingService.kb.trackPOExtracted(extractedPO);

        const updatedPO = Array.from(new Set([...getPOValues(), ...extractedPO]));
        await salesApiService.aiAgent.knowledgeBase.updateProductAndOfferings(updatedPO);

        queryClient.setQueryData(
            aiAgentKnowledgeBaseQueryKeys.customization(),
            (oldData: KnowledgeBaseResponse): KnowledgeBaseResponse => {
                return {
                    model: {
                        ...oldData.model,
                        productAndOfferings: updatedPO,
                    },
                    files: oldData.files.map((file) => ({
                        ...file,
                        isProcessing: file.id === fileId ? false : file.isProcessing,
                    })),
                };
            },
        );

        showMessage();
    } catch (error) {
        AIAgentTrackingService.kb.trackFileUpload(file.name, "failure");
        Sentry.error("Error uploading file to AI Agent Knowledge Base", error);
    }
};
